import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { GRACE } from "@/constants/testIds";
import { ShieldCheck } from "lucide-react";

/**
 * SafetyNetBanner
 *
 * Renders one of three states based on GET /api/subscription/grace-status:
 *   - eligible  → amber "you can still cancel & refund" panel with the
 *     countdown and a Cancel-refund button (POST /grace-cancel).
 *   - not eligible (content_accessed / expired) → muted informational.
 *   - already_refunded → success panel with refund receipt.
 *
 * Renders nothing when the user has no paid transaction (reason=no_paid_transaction).
 */
export default function SafetyNetBanner({ onRefunded }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/subscription/grace-status")
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus(null));
  };

  useEffect(() => { load(); }, []);

  if (!status || status.reason === "no_paid_transaction") return null;

  // Hide the expired / content-accessed state entirely — this card only
  // matters while the refund window is still actionable. Once the window
  // closes we keep the "already refunded" success state (as a receipt)
  // but drop the muted "safety net closed" panel that otherwise sits on
  // /account forever demanding attention with nothing to act on.
  if (!status.eligible && status.reason !== "already_refunded") return null;

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : "";

  const cancel = async () => {
    const msg = t("safetyNet.confirming", {
      tier: (status.tier || "").toString().toUpperCase(),
      amount: (status.amount || 0).toFixed(2),
    });
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.post("/subscription/grace-cancel");
      toast.success(t("safetyNet.cancelSuccess"));
      load();
      if (onRefunded) onRefunded();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("safetyNet.cancelError"));
    } finally {
      setBusy(false);
    }
  };

  if (status.reason === "already_refunded") {
    return (
      <div data-testid={GRACE.banner} className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 flex items-start gap-4">
        <div className="text-emerald-600 mt-0.5"><ShieldCheck size={22} /></div>
        <div>
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold">{t("safetyNet.eyebrow")}</div>
          <div className="mt-1 font-display font-bold text-lg text-emerald-900">{t("safetyNet.refundedTitle")}</div>
          <p className="mt-1 text-sm text-emerald-900/80">
            {t("safetyNet.refundedBody", { amount: (status.amount || 0).toFixed(2), refundedAt: fmtDate(status.refunded_at) })}
          </p>
        </div>
      </div>
    );
  }

  if (status.eligible) {
    return (
      <div data-testid={GRACE.banner} className="bg-amber-50 border-l-4 border-amber-400 rounded-r-2xl p-6 flex flex-col sm:flex-row items-start gap-4">
        <div className="text-amber-600 mt-0.5"><ShieldCheck size={22} /></div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-amber-700 font-semibold">{t("safetyNet.eyebrow")}</div>
          <div className="mt-1 font-display font-bold text-lg text-amber-900">{t("safetyNet.activeTitle")}</div>
          <p className="mt-1 text-sm text-amber-900/80">
            {t("safetyNet.activeBody", {
              expiresAt: fmtDate(status.expires_at),
              hoursRemaining: status.hours_remaining,
            })}
          </p>
          <div data-testid={GRACE.countdown} className="mt-3 inline-flex items-baseline gap-2 bg-white/70 border border-amber-200 rounded-lg px-3 py-1.5">
            <span className="font-display font-black text-2xl text-amber-900">{status.hours_remaining}</span>
            <span className="text-xs uppercase tracking-widest text-amber-700 font-semibold">h left</span>
          </div>
        </div>
        <Button
          data-testid={GRACE.cancelButton}
          disabled={busy}
          onClick={cancel}
          className="bg-slate-900 hover:bg-slate-800 text-white h-11 px-5"
        >
          {t("safetyNet.cancelButton")}
        </Button>
      </div>
    );
  }

  // Expired / content-accessed panels are intentionally omitted — see
  // the early-return above. If we're here, the caller is in an active
  // grace window OR already refunded, both of which we've rendered.
  return null;
}
